import { type QueryObserverResult, type UseQueryOptions, useQuery } from '@tanstack/react-query';
import { getPrComments, getUnifiedProcessGraph } from './unified-process-graph-client';
import type { PrCommentsResponse, UnifiedProcessGraphResponse } from './unified-process-graph-client.type';

/**
 * Query manager for fetching unified process graph data for a task
 *
 * @param {string | undefined} taskId - The UUID of the task
 * @param {UseQueryOptions<UnifiedProcessGraphResponse>} options - Additional options for the query (optional)
 * @return {{ data: UnifiedProcessGraphResponse | undefined, query: QueryObserverResult<UnifiedProcessGraphResponse> }}
 */
const useUnifiedProcessGraph = (
  taskId: string | undefined,
  options?: UseQueryOptions<UnifiedProcessGraphResponse>,
): { data: UnifiedProcessGraphResponse | undefined; query: QueryObserverResult<UnifiedProcessGraphResponse> } => {
  const query = useQuery({
    queryKey: ['unified-process-graph', taskId] as const,
    queryFn: () => {
      if (!taskId) {
        return Promise.reject('Task ID is required');
      }
      return getUnifiedProcessGraph(taskId);
    },
    enabled: !!taskId,
    ...options,
  });

  return { data: query.data, query };
};

/**
 * Query manager for a task's pull request with its full review comments, for
 * the graph's comment panel. Keyed under the graph's own key, so invalidating a
 * task's graph also refreshes its comments. Fetches only when both ids are set.
 *
 * @param {string | undefined} taskId - The UUID of the task
 * @param {string | undefined} changeRequestId - The pull request's graph id (`cr_<uuid>`)
 * @param {UseQueryOptions<PrCommentsResponse>} options - Additional options for the query (optional)
 * @return {{ data: PrCommentsResponse | undefined, query: QueryObserverResult<PrCommentsResponse> }}
 */
const usePrComments = (
  taskId: string | undefined,
  changeRequestId: string | undefined,
  options?: Partial<UseQueryOptions<PrCommentsResponse>>,
): { data: PrCommentsResponse | undefined; query: QueryObserverResult<PrCommentsResponse> } => {
  const query = useQuery({
    queryKey: ['unified-process-graph', taskId, 'pr-comments', changeRequestId] as const,
    queryFn: () => {
      if (!taskId || !changeRequestId) {
        return Promise.reject('Task ID and change request ID are required');
      }
      return getPrComments(taskId, changeRequestId);
    },
    enabled: !!taskId && !!changeRequestId,
    ...options,
  });

  return { data: query.data, query };
};

export { usePrComments, useUnifiedProcessGraph };
